/* eslint-disable max-len */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { GameConceptIdSearchParam } from "../../helpers/dataFetchingHelpers";
import {
  GameConceptApplication,
  isGameConceptSoftRejected,
} from "../../helpers/gameConceptHelpers";
import { calculateSavedGameConceptProgress } from "../../helpers/gameConceptProgress";
import { getDashboardUrl } from "../../helpers/navigationHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { CrmGameConceptStatus, CrmGameConceptSubStatus } from "../../models/crmStatus";
import { Programs } from "../../models/programsConfig";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { CrmActions } from "../../redux/actions/crmApiActions";
import { Modal } from "../Modal/Modal";
import * as styles from "./GameConceptsManager.module.css";

type GameConceptListRowProps = Readonly<{
  programDashboardRouteName: RouteNames;
  gameConcept: GameConceptApplication;
  program: Programs;
  onConceptClick?: (gameConceptId: string) => void;
}>;

type StatusInfo = {
  label: string;
  dotClass: string;
  labelClass: string;
  datePrefix?: string;
};

type StatusLabels = Readonly<{
  draft: string;
  underReview: string;
  approved: string;
  contractsSent: string;
  rejected: string;
  needsAttention: string;
  onHold: string;
  submitted: string;
  reviewed: string;
}>;

const getStatusInfo = (
  status: CrmGameConceptStatus,
  _subStatus: CrmGameConceptSubStatus,
  labels: StatusLabels
): StatusInfo => {
  switch (status) {
    case CrmGameConceptStatus.Unset:
      return {
        label: labels.draft,
        dotClass: styles.dotDraft,
        labelClass: styles.statusDraft,
      };
    case CrmGameConceptStatus.UnderReview:
      return {
        label: labels.underReview,
        dotClass: styles.dotReview,
        labelClass: styles.statusReview,
        datePrefix: labels.submitted,
      };
    case CrmGameConceptStatus.Accepted:
      return {
        label: labels.approved,
        dotClass: styles.dotApproved,
        labelClass: styles.statusApproved,
        datePrefix: labels.reviewed,
      };
    case CrmGameConceptStatus.AdditionalContractsSent:
      return {
        label: labels.contractsSent,
        dotClass: styles.dotReview,
        labelClass: styles.statusReview,
        datePrefix: labels.reviewed,
      };
    case CrmGameConceptStatus.Rejected:
      return {
        label: labels.rejected,
        dotClass: styles.dotRejected,
        labelClass: styles.statusRejected,
        datePrefix: labels.reviewed,
      };
    case CrmGameConceptStatus.MoreInfoNeeded:
      return {
        label: labels.needsAttention,
        dotClass: styles.dotWarning,
        labelClass: styles.statusWarning,
        datePrefix: labels.reviewed,
      };
    case CrmGameConceptStatus.OnHold:
      return {
        label: labels.onHold,
        dotClass: styles.dotReview,
        labelClass: styles.statusReview,
        datePrefix: labels.reviewed,
      };
    default:
      return {
        label: labels.draft,
        dotClass: styles.dotDraft,
        labelClass: styles.statusDraft,
      };
  }
};

export const GameConceptListRow = ({
  gameConcept,
  program,
  programDashboardRouteName,
  onConceptClick,
}: GameConceptListRowProps) => {
  const [
    untitledGame,
    resubmitNote,
    draftLabel,
    underReviewLabel,
    approvedLabel,
    contractsSentLabel,
    rejectedLabel,
    needsAttentionLabel,
    onHoldLabel,
    submittedPrefix,
    reviewedPrefix,
    moreOptionsLabel,
    deleteLabel,
    deleteTitle,
    closeLabel,
    deleteBody,
    cancelLabel,
    progressCompleteLabel,
    readyForIdXboxApplicationLabel,
  ] = useStrings(
    "untitledGame",
    "gameConceptSoftRejectedResubmitNote",
    "draft",
    "underReview",
    "approved",
    "contractsSentLabel",
    "rejected",
    "needsAttentionLabel",
    "onHold",
    "submitted",
    "reviewed",
    "moreOptionsAriaLabel",
    "deleteButton",
    "deleteGameConceptTitle",
    "closeAriaLabel",
    "deleteGameConceptConfirmBody",
    "cancelButton",
    "gameConceptProgressComplete",
    "gameConceptReadyForIdXboxApplication"
  );
  const history = useHistory();
  const dispatch = useDispatch();
  const [progressPercentage, setProgressPercentage] = useState<number>();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const gameConceptInfo = gameConcept.gameConceptInfo;
  const gameState = gameConceptInfo.state;
  const gameStatus = gameState.status;
  const gameSubStatus = gameState.subStatus;
  const gameTitle = gameConceptInfo.details.gameTitle;

  useEffect(() => {
    if (gameStatus !== CrmGameConceptStatus.Unset) {
      setProgressPercentage(undefined);
      return;
    }

    let isCurrent = true;

    calculateSavedGameConceptProgress(program, gameConceptInfo).then(progress => {
      if (isCurrent) {
        setProgressPercentage(progress.percentage);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [gameConceptInfo, gameStatus, program]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const {
    label: statusLabel,
    dotClass,
    labelClass,
    datePrefix,
  } = getStatusInfo(gameStatus, gameSubStatus, {
    draft: draftLabel,
    underReview: underReviewLabel,
    approved: approvedLabel,
    contractsSent: contractsSentLabel,
    rejected: rejectedLabel,
    needsAttention: needsAttentionLabel,
    onHold: onHoldLabel,
    submitted: submittedPrefix,
    reviewed: reviewedPrefix,
  });

  const viewOnlyGame =
    gameStatus === CrmGameConceptStatus.UnderReview ||
    gameStatus === CrmGameConceptStatus.AdditionalContractsSent ||
    gameStatus === CrmGameConceptStatus.OnHold ||
    gameStatus === CrmGameConceptStatus.Accepted ||
    (gameStatus === CrmGameConceptStatus.Rejected &&
      gameSubStatus !== CrmGameConceptSubStatus.RejectedSoftGeneral &&
      gameSubStatus !== CrmGameConceptSubStatus.RejectedSoftAlreadyHaveDevToken);

  const canDelete = gameStatus === CrmGameConceptStatus.Unset;
  const isSoftRejected = isGameConceptSoftRejected(gameStatus, gameSubStatus);

  const handleClick = () => {
    if (onConceptClick) {
      onConceptClick(viewOnlyGame ? gameConcept.id : gameState.id);
    } else if (viewOnlyGame) {
      let url = "";
      switch (program) {
        case Programs.Aaa:
          url = OnboardingUrls[RouteNames.AaaFormPreview];
          break;
        case Programs.IdXbox:
          url = OnboardingUrls[RouteNames.IdXboxFormPreview];
          break;
      }
      history.push({
        pathname: url,
        search: `?${GameConceptIdSearchParam}=${gameConcept.id}`,
      });
    } else {
      history.push(getDashboardUrl(programDashboardRouteName, gameState.id));
    }
  };

  const handleRowKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) {
      return;
    }

    e.preventDefault();
    handleClick();
  };

  const handleMenuClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(prev => !prev);
  }, []);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setShowDeleteModal(false);
    dispatch({
      type: CrmActions.DELETE_GAME_CONCEPT_START,
      program,
      gameConceptId: gameConcept.id,
      redirectToDashboard: false,
    });
  }, [dispatch, program, gameConcept.id]);

  const displaySubmissionDate = gameState.hubSubmissionDate ?? gameState.submittedDate;
  const formattedDate = displaySubmissionDate
    ? new Date(displaySubmissionDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const displayTitle = gameTitle || untitledGame;
  const progressLabel = progressCompleteLabel.replace("{0}", progressPercentage?.toString() ?? "0");
  const isReadyForIdXboxApplication = progressPercentage === 100 && program === Programs.IdXbox;

  return (
    <>
      <div
        className={styles.conceptRow}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleRowKeyDown}
      >
        <div className={styles.conceptInfo}>
          <div className={styles.conceptMeta}>
            <span className={`${styles.statusDot} ${dotClass}`} />
            <span className={`${styles.statusLabel} ${labelClass}`}>{statusLabel}</span>
          </div>
          <h3 className={styles.conceptTitle}>{displayTitle}</h3>
          {isSoftRejected && <span className={styles.resubmitNote}>{resubmitNote}</span>}
        </div>
        {canDelete && (
          <div className={styles.moreMenuWrapper} ref={menuRef}>
            <button
              type="button"
              className={styles.moreButton}
              onClick={handleMenuClick}
              aria-label={moreOptionsLabel}
            >
              ···
            </button>
            {showMenu && (
              <div className={styles.moreMenu}>
                <button type="button" className={styles.moreMenuItem} onClick={handleDeleteClick}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M6.5 2h3a.5.5 0 0 1 .5.5V3H6v-.5a.5.5 0 0 1 .5-.5ZM5 3v-.5A1.5 1.5 0 0 1 6.5 1h3A1.5 1.5 0 0 1 11 2.5V3h3.5a.5.5 0 0 1 0 1H13.9l-.6 8.5A1.5 1.5 0 0 1 11.8 14H4.2a1.5 1.5 0 0 1-1.5-1.5L2.1 4H1.5a.5.5 0 0 1 0-1H5Zm1.5 3.5a.5.5 0 0 0-1 0v5a.5.5 0 0 0 1 0v-5Zm3 0a.5.5 0 0 0-1 0v5a.5.5 0 0 0 1 0v-5Z"
                      fill="#e07070"
                    />
                  </svg>
                  {deleteLabel}
                </button>
              </div>
            )}
          </div>
        )}
        {progressPercentage !== undefined && (
          <div className={styles.conceptProgress}>
            <div className={styles.conceptProgressLabel}>
              {isReadyForIdXboxApplication && <span>{readyForIdXboxApplicationLabel}</span>}
              <span>{progressLabel}</span>
            </div>
            <progress
              aria-label={progressLabel}
              className={styles.conceptProgressBar}
              max={100}
              value={progressPercentage}
            />
          </div>
        )}
        {formattedDate && datePrefix && (
          <span className={styles.lastEdited}>
            {datePrefix} {formattedDate}
          </span>
        )}
      </div>
      {showDeleteModal && (
        <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
          <div className={styles.deleteModalHeader}>
            <h2 className={styles.deleteModalTitle}>{deleteTitle}</h2>
            <button
              type="button"
              className={styles.deleteModalClose}
              onClick={() => setShowDeleteModal(false)}
              aria-label={closeLabel}
            >
              ✕
            </button>
          </div>
          <div className={styles.deleteModalBody}>{deleteBody.replace("{0}", displayTitle)}</div>
          <div className={styles.deleteModalActions}>
            <button
              type="button"
              className={styles.deleteModalCancel}
              onClick={() => setShowDeleteModal(false)}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className={styles.deleteModalConfirm}
              onClick={handleConfirmDelete}
            >
              {deleteLabel}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};
